import React, { useRef, useState } from "react";
import styled from "styled-components";

import { Project } from "../../../app/services/projects";
import TakeLabel from "../../../components/label/TakeLabel";
// @ts-ignore -- JS モジュール（型定義なし）
import { useHlsPlayer, isHlsSource } from "../../../hooks/useHlsPlayer";

/**
 * プロジェクトリスト「進行中」タブのホイール（ProjectWheel）に並べる縦型大判カード。
 *
 * オファー作成ウィザードのロールカード（RolePicker の PortraitCard）と同じ
 * マガジン風レイヤー構成:
 *  - cover: banner_image_optimized を全面に敷く（無ければ banner-color 背景）
 *  - ロゴ: banner_logo（透過 PNG マスク）を下部左右中央にレイヤー
 *          （無ければ code_name を banner_font のフォントで表示）
 *  - 動画: バナー画像が無いプロジェクトで videoUrl が渡された場合、
 *          動画配信サービス風に背景でミュートループ cover 再生する
 *  - 右上: 自分のバッジ合計（やること + 未読 + 未DL）の垂れ帯
 *          （HOME ホイールカードと同じ TakeLabel）
 *
 * サイズは持たない（width/height: 100%）。実寸は RadioCardGroup の Slot が
 * cardSize で決めるため、内部レイアウトだけに責任を持つ。
 * Slot 側が role=radio + onClick を持つので、ここでは button を使わない。
 */

// ===== レイヤー構成 =====

const Frame = styled.div<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  outline: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--soremo-blue)" : "var(--soremo-border)"};
  background: linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%);
  overflow: hidden;
  transition: border-color 0.2s, outline-color 0.2s;
`;

const CoverImage = styled.div<{ $url: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $url }) => $url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

/**
 * 画像なしプロジェクトのフォールバック背景。`banner-color{N}` クラスが
 * background と `div { color: white }` を与えるため、内側のロゴ
 * （currentColor マスク）/ コードネームは自動で視認色になる。
 */
const CoverFallback = styled.div`
  position: absolute;
  inset: 0;
`;

/** バナー未設定プロジェクトの背景動画。読み込み完了までは透明にして
 *  banner-color 背景からフェードインさせる（Netflix / Apple TV 風）。 */
const CoverVideoEl = styled.video<{ $ready: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  transition: opacity 0.6s ease;
`;

/**
 * バナー未設定プロジェクトの背景動画コンポーネント。
 * HOME 進行中ホイール（ShotPortraitCard）の選択中カード自動再生でも共用する。
 */
export const CoverVideo: React.FC<{ src: string; poster?: string }> = ({
  src,
  poster,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  // .m3u8 は useHlsPlayer 経由（Safari: ネイティブ / その他: hls.js）で再生する。
  const isHls = isHlsSource(src);
  useHlsPlayer(src, videoRef, isHls);

  return (
    <CoverVideoEl
      ref={videoRef}
      $ready={ready}
      src={isHls ? undefined : src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      onCanPlay={() => setReady(true)}
    />
  );
};

/** cover 画像上でロゴ / コードネームの視認性を確保する下部スクリム。 */
const BottomScrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 38.2%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
`;

/** ロゴ / コードネームを「下の左右中央」に置く配置レイヤー。 */
const LogoLayer = styled.div`
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  width: 80%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: none;
`;

/**
 * AI 生成の透過ロゴを単色で塗って表示する（Projects.tsx の CardBannerLogo と
 * 同じ mask 方式）。cover 画像上は白、banner-color 背景上は currentColor
 * （= banner-color 側が指定する文字色）で塗る。
 */
const LogoMask = styled.div<{ $url: string; $onImage: boolean }>`
  width: 61.8%;
  height: 56px;
  background-color: ${({ $onImage }) => ($onImage ? "#ffffff" : "currentColor")};
  mask: url("${({ $url }) => $url}") center bottom / contain no-repeat;
  -webkit-mask: url("${({ $url }) => $url}") center bottom / contain no-repeat;
`;

const CodeName = styled.div.attrs<{ $bannerFont: string; $onImage: boolean }>(
  (props) => ({
    // "logo" はロゴ画像表示用の特殊値。ロゴ未設定でここに来た場合はフォント 1 に落とす。
    className: `code-font${/^\d+$/.test(props.$bannerFont || "") ? props.$bannerFont : 1}`,
  }),
)<{ $bannerFont: string; $onImage: boolean }>`
  overflow: hidden;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  ${({ $onImage }) => $onImage && "color: #ffffff;"}
`;

// ===== プロジェクトカード =====

export interface ProjectPortraitCardProps {
  project: Project;
  selected: boolean;
  /**
   * バナー画像が無いプロジェクト向けの背景動画（進行中ショットの最新動画）。
   * 渡された場合のみ banner-color 背景の上でミュートループ cover 再生する。
   * 選択中カードだけに渡す想定（ProjectWheel 側で制御）。
   */
  videoUrl?: string;
  /** 背景動画のポスター（ショットのサムネイル）。 */
  videoPoster?: string;
  /**
   * 自分のバッジ合計（やること + トークルーム未読 + 未ダウンロード）。
   * 1 以上のときだけ右上の垂れ帯（TakeLabel）に表示する。
   */
  badgeCount?: number;
}

const ProjectPortraitCard: React.FC<ProjectPortraitCardProps> = ({
  project,
  selected,
  videoUrl,
  videoPoster,
  badgeCount = 0,
}) => {
  const hasImage = Boolean(project.banner_image_optimized);
  const hasVideo = !hasImage && Boolean(videoUrl);
  const showLogo =
    project.banner_font === "logo" && Boolean(project.banner_logo);

  const logoOrCodeName = (onImage: boolean) =>
    showLogo ? (
      <LogoMask
        $url={project.banner_logo as string}
        $onImage={onImage}
        role="img"
        aria-label={project.code_name}
      />
    ) : (
      <CodeName $bannerFont={project.banner_font} $onImage={onImage}>
        {project.code_name}
      </CodeName>
    );

  return (
    <Frame $selected={selected}>
      {badgeCount > 0 && (
        <TakeLabel
          label={String(badgeCount)}
          labelColor="blue"
          labelSize="large"
          ariaLabel={`未対応 ${badgeCount}件`}
        />
      )}
      {hasImage ? (
        <>
          <CoverImage $url={project.banner_image_optimized as string} />
          <BottomScrim />
          <LogoLayer>{logoOrCodeName(true)}</LogoLayer>
        </>
      ) : (
        // 動画あり: banner-color 背景の上で cover 再生（ロード完了までは背景色が
        // 見える）。ロゴ / コードネームは cover 画像時と同じ白 + スクリムで描く。
        <CoverFallback className={`banner-color${project.banner_color || 1}`}>
          {hasVideo && (
            <>
              <CoverVideo src={videoUrl as string} poster={videoPoster} />
              <BottomScrim />
            </>
          )}
          <LogoLayer>{logoOrCodeName(hasVideo)}</LogoLayer>
        </CoverFallback>
      )}
    </Frame>
  );
};

// ===== 新規プロジェクト作成カード =====

const NewProjectBody = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
`;

const NewProjectIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $active: boolean }>`
  font-size: 48px;
  color: ${({ $active }) =>
    $active ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  transition: color 0.2s;
`;

export interface NewProjectCardProps {
  selected: boolean;
}

/**
 * ホイール末尾に常駐する「新しいプロジェクトを始める」カード。
 * オファー作成の「メールアドレスで招待」カードと同じセンチネルパターンで、
 * 実際の遷移はアクションパネル側のボタン（/project-creation）が担う。
 */
export const NewProjectCard: React.FC<NewProjectCardProps> = ({ selected }) => (
  <Frame $selected={selected}>
    <NewProjectBody>
      <NewProjectIcon $active={selected}>add_circle</NewProjectIcon>
      <span className="heading-spacing-18 u-line-height-150">
        新しいプロジェクトを始める
      </span>
      <span className="bodytext-11 u-text-light-gray u-line-height-150">
        コードネームと目的物を決めて開始します
      </span>
    </NewProjectBody>
  </Frame>
);

export default ProjectPortraitCard;
